import type { components } from "@/api-schema";
import { useVehicleValuationConfirmerSearchQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-confirmer-search.query";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

const FILTER_PAGE_SIZE = 50;
type VehicleValuationFilterUser = Pick<components["schemas"]["User"], "firstName" | "lastName" | "email">;

function getUserLabel(user: VehicleValuationFilterUser): string {
  return getFullUserName({ user });
}

function useVehicleValuationConfirmerFilterData(): {
  data: FilterData;
  pagination: FilterPaginationState;
} {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: users,
    isPending: isLoading,
    error,
  } = useVehicleValuationConfirmerSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
    },
  });

  const options =
    users?.data
      .map((user) => ({
        id: user.uuid ?? "",
        name: getUserLabel(user),
      }))
      .filter((option) => option.id && option.name) ?? [];

  const loadedCount = users?.data?.length ?? 0;
  const total = users?.meta?.total ?? 0;
  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: { options, isLoading, error },
    pagination,
  };
}

export class VehicleValuationConfirmerFilter extends Filter<Record<string, never>, unknown> {
  readonly id: string;
  readonly labelSingular = "common:User";
  readonly labelPlural = "common:Users";
  readonly placeholder = "common:SelectUser";
  readonly searchPlaceholder = "common:SearchUser";
  readonly emptyText = "common:NoUsersFound";

  constructor({ id }: { id: string }) {
    super();
    this.id = id;
    this.body = {};
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleValuationConfirmerFilterComponent filter={this} {...props} />;
  }
}

function VehicleValuationConfirmerFilterComponent({
  filter,
  ...props
}: { filter: VehicleValuationConfirmerFilter } & BaseFilterProps) {
  const { data, pagination } = useVehicleValuationConfirmerFilterData();

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
